import React, { useState, useEffect } from 'react';
import SeoMeta from '../components/SeoMeta';
import Lightbox from '../components/Lightbox';
import { getCdnImage } from '../lib/imageUtils';

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGallerySlug, setSelectedGallerySlug] = useState('alle');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    async function loadGalleries() {
      try {
        setLoading(true);
        const res = await fetch('https://api.dev.auxo.dk/public/content/galleri');
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : data.items || [];
          setGalleries(items);
        }
      } catch (err) {
        // Quiet
      } finally {
        setLoading(false);
      }
    }
    loadGalleries();
  }, []);

  const openLightbox = (imgs, idx = 0) => {
    setLightboxImages(imgs);
    setActiveImageIndex(idx);
    setLightboxOpen(true);
  };

  const activeGalleries =
    selectedGallerySlug === 'alle'
      ? galleries
      : galleries.filter((g) => {
          const slug = typeof g.slug === 'object' ? g.slug?._ || g.slug?.da : g.slug;
          return slug === selectedGallerySlug;
        });

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Galleri - ENTREPRENØR LANG ApS"
        description="Billedgalleri over entreprenørarbejde, maskiner og udførte opgaver hos ENTREPRENØR LANG ApS."
        keywords="galleri, entreprenør lang, billeder, maskiner, kloak, vandværk, belægning, snerydning"
      />

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-601820261-1366670001827292-7586379579870585065-n.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Galleri baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3 border border-emerald-500/30">
              <i className="fa-solid fa-camera" aria-hidden="true" />
              Billedarkiv
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              Galleri
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-3 leading-relaxed">
              Her finder du fotodokumentation af vores entreprenørarbejde, materiel og opgaver opdelt efter fagområde.
            </p>
          </div>
        </div>
      </section>

      {/* Filter tabs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Mobile dropdown filter */}
        <div className="sm:hidden pb-6 border-b border-slate-200">
          <label htmlFor="gallery-mobile-filter" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            Vælg galleri
          </label>
          <div className="relative">
            <select
              id="gallery-mobile-filter"
              value={selectedGallerySlug}
              onChange={(e) => setSelectedGallerySlug(e.target.value)}
              className="w-full appearance-none bg-white border border-slate-300 text-slate-900 font-semibold rounded-xl px-4 py-3 pr-10 shadow-xs focus:outline-hidden focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600"
            >
              <option value="alle">Alle gallerier</option>
              {galleries.map((g) => {
                const gSlug = typeof g.slug === 'object' ? g.slug?._ || g.slug?.da : g.slug;
                return (
                  <option key={g.id || gSlug} value={gSlug}>
                    {g.title}
                  </option>
                );
              })}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-600">
              <i className="fa-solid fa-chevron-down text-sm" aria-hidden="true" />
            </div>
          </div>
        </div>

        {/* Desktop pill tabs */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 pb-6 border-b border-slate-200">
          <button
            onClick={() => setSelectedGallerySlug('alle')}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
              selectedGallerySlug === 'alle'
                ? 'bg-emerald-700 text-white shadow'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Alle gallerier
          </button>

          {galleries.map((g) => {
            const gSlug = typeof g.slug === 'object' ? g.slug?._ || g.slug?.da : g.slug;
            return (
              <button
                key={g.id || gSlug}
                onClick={() => setSelectedGallerySlug(gSlug)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                  selectedGallerySlug === gSlug
                    ? 'bg-emerald-700 text-white shadow'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {g.title}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="py-20 text-center text-slate-500">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-emerald-600 mb-2" aria-hidden="true" />
            <p className="text-sm font-medium">Henter billeder...</p>
          </div>
        )}

        {/* Gallery collections */}
        {!loading && (
          <div className="space-y-16 mt-10">
            {activeGalleries.map((gal) => {
              const galSlug = typeof gal.slug === 'object' ? gal.slug?._ || gal.slug?.da : gal.slug;
              const images = gal.images || [];

              return (
                <div key={gal.id || galSlug} className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 border-b border-slate-200 pb-4">
                    <div>
                      <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                        {gal.title}
                      </h2>
                      {gal.description && (
                        <p className="text-slate-600 text-sm mt-1">{gal.description}</p>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200 self-start sm:self-auto">
                      {images.length} billeder
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => openLightbox(images, idx)}
                        className="group relative h-56 rounded-xl overflow-hidden bg-slate-100 cursor-pointer border border-slate-200 shadow-sm hover:shadow-md transition-all aspect-4/3"
                      >
                        <img
                          src={getCdnImage(img, { width: 600, height: 450, fit: 'cover' })}
                          alt={`${gal.title} - foto ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 rounded-full bg-white/90 text-emerald-700 flex items-center justify-center shadow-md">
                            <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setActiveImageIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
      />
    </div>
  );
}
